import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import './RideStoryDetail.css';

interface StoryItem {
  id: number;
  type: string;
  user_id: number;
  user_name: string;
  profile_picture: string | null;
  content: string | null;
  audio_url: string | null;
  audio_duration_s: number | null;
  transcript: string | null;
  image_url: string | null;
  latitude: number | null;
  longitude: number | null;
  relative_minutes: number | null;
  created_at: string | null;
}

interface ParticipantSummary {
  user_id: number;
  name: string;
  distance: number;
  elevation: number;
}

interface StoryData {
  ride_story_id: number;
  ride_id: number;
  title: string;
  cover_image_url: string | null;
  item_count: number;
  published_at: string;
  items: StoryItem[];
  summary: {
    participant_count: number;
    total_distance: number;
    total_elevation: number;
    duration_minutes: number;
    participants: ParticipantSummary[];
  };
}

interface RideStoryDetailProps {
  rideId: number;
  onClose: () => void;
}

const AudioPlayer: React.FC<{ audioUrl: string; durationS: number }> = ({ audioUrl, durationS }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const toggle = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.ontimeupdate = () => {
        if (audioRef.current) {
          setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
        }
      };
      audioRef.current.onended = () => { setPlaying(false); setProgress(0); };
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  }, [audioUrl, playing]);

  return (
    <div className="story-audio-player">
      <button className="story-audio-play-btn" onClick={toggle}>
        {playing ? '\u23F8' : '\u25B6'}
      </button>
      <div className="story-audio-progress">
        <div className="story-audio-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <span className="story-audio-duration">{formatTime(durationS)}</span>
    </div>
  );
};

const RideStoryDetail: React.FC<RideStoryDetailProps> = ({ rideId, onClose }) => {
  const { t } = useLanguage();
  const [story, setStory] = useState<StoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStory = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/feed/ride-story/${rideId}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to load story');
        const data = await res.json();
        setStory(data.story);
      } catch {
        setError(t('feed.rideStory.errorLoad'));
      } finally {
        setLoading(false);
      }
    };
    fetchStory();
  }, [rideId, t]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const formatDistance = (meters?: number) => {
    if (!meters) return '0 km';
    return `${(meters / 1000).toFixed(1)} km`;
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '0h';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const formatRelativeTime = (minutes: number | null) => {
    if (minutes === null || minutes === undefined) return '';
    if (minutes < 0) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `+${h}h${m > 0 ? ` ${m}m` : ''}`;
    return `+${m}m`;
  };

  const renderStoryItem = (item: StoryItem) => {
    return (
      <div key={item.id} className="story-timeline-item">
        <div className="story-timeline-dot" />
        <div className="story-timeline-content">
          <div className="story-item-header">
            {item.profile_picture ? (
              <img src={item.profile_picture} alt="" className="story-item-avatar" />
            ) : (
              <div className="story-item-avatar-placeholder">
                {item.user_name?.[0] || '?'}
              </div>
            )}
            <div className="story-item-user-info">
              <span className="story-item-user-name">{item.user_name}</span>
              {item.relative_minutes !== null && (
                <span className="story-item-time">{formatRelativeTime(item.relative_minutes)}</span>
              )}
            </div>
          </div>

          {item.type === 'voice' && (
            <div className="story-item-body">
              {item.audio_url && item.audio_duration_s && (
                <AudioPlayer audioUrl={item.audio_url} durationS={item.audio_duration_s} />
              )}
              {item.transcript && (
                <p className="story-item-transcript">{item.transcript}</p>
              )}
            </div>
          )}

          {item.type === 'photo' && (
            <div className="story-item-body">
              {item.image_url && (
                <img src={item.image_url} alt="" className="story-item-image" loading="lazy" />
              )}
              {item.content && (
                <p className="story-item-text">{item.content}</p>
              )}
            </div>
          )}

          {item.type === 'text' && item.content && (
            <div className="story-item-body">
              <p className="story-item-text">{item.content}</p>
            </div>
          )}

          {item.type === 'checkin' && (
            <div className="story-item-body">
              <p className="story-item-text">{item.content || t('feed.rideStory.checkin')}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="story-modal-overlay" onClick={onClose}>
      <div className="story-modal" onClick={e => e.stopPropagation()}>
        <button className="story-modal-close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {loading && (
          <div className="story-modal-loading">
            <div className="story-loading-spinner" />
            <p>{t('common.loading')}</p>
          </div>
        )}

        {error && (
          <div className="story-modal-error">
            <p>{error}</p>
            <button onClick={onClose}>{t('common.close')}</button>
          </div>
        )}

        {story && !loading && !error && (
          <div className="story-modal-content">
            {/* Cover */}
            <div className="story-cover-section">
              {story.cover_image_url ? (
                <img src={story.cover_image_url} alt="" className="story-cover-image" />
              ) : (
                <div className="story-cover-fallback" />
              )}
              <div className="story-cover-overlay">
                <h2 className="story-cover-title">{story.title}</h2>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="story-summary-stats">
              <div className="story-stat">
                <span className="story-stat-value">{formatDistance(story.summary.total_distance)}</span>
                <span className="story-stat-label">{t('feed.rideStory.distance')}</span>
              </div>
              <div className="story-stat">
                <span className="story-stat-value">{Math.round(story.summary.total_elevation || 0)} m</span>
                <span className="story-stat-label">{t('feed.rideStory.elevation')}</span>
              </div>
              <div className="story-stat">
                <span className="story-stat-value">{formatDuration(story.summary.duration_minutes)}</span>
                <span className="story-stat-label">{t('feed.rideStory.duration')}</span>
              </div>
              <div className="story-stat">
                <span className="story-stat-value">{story.summary.participant_count}</span>
                <span className="story-stat-label">{t('feed.rideStory.participants')}</span>
              </div>
            </div>

            {/* Timeline */}
            {story.items.length > 0 && (
              <div className="story-timeline">
                <h3 className="story-timeline-title">
                  {story.item_count} {t('feed.rideStory.moments')}
                </h3>
                {story.items.map(renderStoryItem)}
              </div>
            )}

            {/* Participants */}
            {story.summary.participants.length > 0 && (
              <div className="story-participants">
                <h3 className="story-participants-title">{t('feed.rideStory.participants')}</h3>
                {story.summary.participants.map(p => (
                  <div key={p.user_id} className="story-participant-row">
                    <span className="story-participant-name">{p.name}</span>
                    <span className="story-participant-stats">
                      {formatDistance(p.distance)} &middot; {Math.round(p.elevation)} m
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RideStoryDetail;
